import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActiveCartService } from '@spartacus/cart/base/core';
import { Cart, MultiCartFacade, OrderEntry } from '@spartacus/cart/base/root';
import { RoutingService } from '@spartacus/core';
import { MediaComponent } from '@spartacus/storefront';
import { Observable, Subscription, Subject } from 'rxjs';
import { catchError, filter, map, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { KnBrAddToCartAlertPopupComponent } from 'src/app/kn-br-product/kn-br-add-to-cart/kn-br-add-to-cart-alert-popup.component';
import { KnBrCommonService } from 'src/app/services/kn-br-common.service';
import { KnBrSalesCheckoutService } from 'src/app/services/kn-br-sales-checkout.service';
import { KnBrCartContextService } from 'src/app/store/kn-br-cart-context/kn-br-cart-context.service';
import { KnBrCustomerContextService } from 'src/app/store/kn-br-customer-context/kn-br-customer-context.service';

interface CartItem {
  id: number;
  cartId: string;
  userId: string;
  salesArea: string;
  name: string;
  partNumber: string;
  brand: string;
  productCode: string;
  salesUnit: string;
  quantity: number;
  price?: number;
  compactThumb?: boolean;
  itemComments?: string;
  entryNumber?: string;
  product?: {
    maxOrderQuantity?: number;
  };
}

@Component({
  selector: 'knbr-sales-cart-rfq-summary',
  imports: [FormsModule, MediaComponent, AsyncPipe],
  templateUrl: './sales-cart-rfq-summary.component.html',
  styleUrl: './sales-cart-rfq-summary.component.scss',
})
export class SalesCartRfqSummaryComponent implements OnInit, OnDestroy {
  cartItems: CartItem[] = [];
  cartId: string = '';
  customerId: string = 'current';
  poNumber = '';
  poNumberError = '';
  checkoutDisabled = false;
  cartContext$: Observable<string> = this.knBrCartContextService.get$;
  miniCart$: Observable<Cart>;
  cartEntries$: Observable<OrderEntry[]>;
  isRfqCart$: Observable<boolean>;
  subscription = new Subscription();
  totalPrice?: any;

  private poNumberSubject$ = new Subject<string>();
  private commentsSubject$ = new Subject<{ item: CartItem; comments: string }>();
  private qtyChangeSubject$ = new Subject<{ item: CartItem; qty: number }>();

  constructor(
    private multiCartFacade: MultiCartFacade,
    private modalService: NgbModal,
    private knBrCartContextService: KnBrCartContextService,
    private knBrCustomerContextService: KnBrCustomerContextService,
    protected commonService: KnBrCommonService,
    private routingService: RoutingService,
    private cdr: ChangeDetectorRef,
    private activeCartService: ActiveCartService,
    private knbrsalesCheckoutService: KnBrSalesCheckoutService,
    protected router: Router,
  ) { }

  ngOnInit(): void {
    this.knBrCustomerContextService.getActiveCustomerNumber().subscribe((customerNumber) => {
      if (customerNumber) {
        this.customerId = customerNumber;
      }
    });

    this.commonService.showSpinner$.next(true);
    this.subscription.add(
      this.cartContext$.subscribe((cartId) => {
        if (!cartId) {
          this.cartItems = [];
          this.commonService.showSpinner$.next(false);
          return;
        }

        this.cartId = cartId;
        this.multiCartFacade.reloadCart(cartId);
        this.miniCart$ = this.multiCartFacade.getCart(cartId).pipe(
          filter((cart) => !!cart),
          map((cart) => cart)
        );
        
        // ✅ Setup isRfqCart$ Observable
        this.isRfqCart$ = this.miniCart$.pipe(
          map((cart) => this.checkIfRfqCart(cart))
        );
        
        this.loadCart();
      }, () => {
        this.commonService.showSpinner$.next(false);
      })
    );

    this.cartEntries$ = this.activeCartService
      .getEntries()
      .pipe(filter((entries) => entries.length > 0));

    this.subscription.add(
      this.poNumberSubject$
        .pipe(
          debounceTime(500),  
          distinctUntilChanged(),  
        )
        .subscribe((poNumber) => {
          this.savePONumber(poNumber);
        })
    );

    this.subscription.add(
      this.commentsSubject$
        .pipe(
          debounceTime(500),  
          distinctUntilChanged((prev, curr) => 
            prev.item.entryNumber === curr.item.entryNumber && 
            prev.comments === curr.comments
          ),  
        )
        .subscribe(({ item, comments }) => {
          this.saveItemComments(item, comments);
        })
    );

    this.subscription.add(
      this.qtyChangeSubject$
        .pipe(
          debounceTime(500),  
          distinctUntilChanged((prev, curr) => 
            prev.item.entryNumber === curr.item.entryNumber && 
            prev.qty === curr.qty
          ),  
        )
        .subscribe(({ item, qty }) => {
          this.updateCartEntryQuantity(item, qty);
        })
    );
  }

  

  private loadCart(): void {
    this.commonService.showSpinner$.next(true);
    this.multiCartFacade.getCart(this.cartId).pipe(
      catchError((error) => {
        return [];
      })
    ).subscribe((cart) => {
      if (cart && Array.isArray(cart.entries)) {
        if (cart.entries.length === 0) {
          window.location.reload();
          return;
        }
        this.poNumber = cart.purchaseOrderNumber || '';
        this.totalPrice = cart.totalPrice;
        this.cartItems = this.mapCartItems(cart);
        this.updateCheckoutDisabled();
      }
      this.commonService.showSpinner$.next(false);
      this.cdr.detectChanges();
    });
  }

  private mapCartItems(cart: any): CartItem[] {
    return cart.entries.map((entry: any, index: number) => ({
      id: entry.entryNumber ?? index,
      cartId: cart.code ?? this.cartId,
      userId: cart.user?.uid ?? '',
      salesArea: entry?.salesArea ?? '',
      name: entry.product?.name ?? '',
      partNumber: entry.product?.code ?? '',
      brand: entry.product?.brandName ?? entry.product?.manufacturer ?? '',
      productCode: entry.product?.code ?? '',
      salesUnit: entry.uom ?? '',
      quantity: entry.quantity ?? 1,
      price: entry.product?.price?.value ?? 0,
      itemComments: entry?.entryNote ?? '',
      entryNumber: entry.entryNumber,
      product: entry.product,
      knbrTotalPrice: entry.knbrTotalPrice ?? null,
      totalPrice: entry.totalPrice ?? null,
    }));
  }

  increaseQuantity(itemId: number): void {
    const item = this.cartItems.find((entry) => entry.id === itemId);
    if (!item) {
      return;
    }

    const nextQuantity = Number(item.quantity) + 1;
    this.updateCartEntryQuantity(item, nextQuantity);
  }

  decreaseQuantity(itemId: number): void {
    const item = this.cartItems.find((entry) => entry.id === itemId);
    if (!item || item.quantity <= 1) {
      return;
    }

    const nextQuantity = Math.max(1, Number(item.quantity) - 1);
    this.updateCartEntryQuantity(item, nextQuantity);
  }

  onQtyChange(itemId: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const nextValue = Number(input.value);

    const item = this.cartItems.find((entry) => entry.id === itemId);
    if (!item) {
      return;
    }
    const nextQuantity = Number.isFinite(nextValue) ? Math.max(1, Math.round(nextValue)) : 1;
    input.value = String(nextQuantity);
    
    item.quantity = nextQuantity;
    this.updateCheckoutDisabled();
    this.cdr.detectChanges();
    
    this.qtyChangeSubject$.next({ item, qty: nextQuantity });
  }

  private updateCartEntryQuantity(item: CartItem, qty: number): void {
    const nextQuantity = Math.max(1, Math.round(qty));

    const userId = 'current';
    const cartId = this.cartId;
    const entryNumber: number = Number(item.entryNumber);

    this.commonService.showSpinner$.next(true);

    this.multiCartFacade.updateEntry(userId, cartId, entryNumber, nextQuantity);
  }

  removeCartItem(itemId: number): void {
    const item = this.cartItems.find((entry) => entry.id === itemId);
    if (!item) {
      return;
    }

    const modalRef = this.modalService.open(KnBrAddToCartAlertPopupComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'kn-br-add-to-cart-alert-modal',
      backdropClass: 'kn-br-add-to-cart-alert-backdrop',
    });
    const instance = modalRef.componentInstance;
    instance.header = 'Remove Cart Item';
    instance.message = `Are you sure you want to remove ${item.name || 'this item'} from your cart?`;
    instance.showConfirmation = true;
    instance.confirmText = 'Yes';
    instance.cancelText = 'No';

    modalRef.result
      .then((result) => {
        if (result?.action === 'confirm') {
          this.confirmRemoveCartItem(itemId);
        }
      })
      .catch(() => { });
  }

  private confirmRemoveCartItem(itemId: number): void {
    const item = this.cartItems.find((entry) => entry.id === itemId);
    if (!item) {
      return;
    }

    const userId = 'current';
    const cartId = this.cartId;
    const entryNumber: number = Number(item.entryNumber);

    this.commonService.showSpinner$.next(true);

    this.cartItems = this.cartItems.filter((entry) => entry.id !== itemId);
    this.updateCheckoutDisabled();
    this.cdr.detectChanges();

    this.multiCartFacade.removeEntry(userId, cartId, entryNumber);
  }

  saveItemComments(item: CartItem, comments?: string): void {
    const trimmedComments = (comments !== undefined ? comments : item?.itemComments)?.trim() || '';

    this.commonService.showSpinner$.next(true);
    this.knbrsalesCheckoutService.updateOverlimitComment(item.entryNumber, trimmedComments,true).subscribe({
      next: () => {
        this.commonService.showSpinner$.next(false);
      },
      error: (error) => {
        this.commonService.showSpinner$.next(false);
      }
    });
  }

  onCommentsInput(item: CartItem, event: Event): void {
    const comments = (event.target as HTMLTextAreaElement).value;
    item.itemComments = comments;
    this.updateCheckoutDisabled();
    this.cdr.detectChanges();
    
    this.commentsSubject$.next({ item, comments });
  }

  onPoNumberInput(event: Event): void {
    this.poNumber = (event.target as HTMLInputElement).value;
    this.poNumberError = '';
    this.updateCheckoutDisabled();
    this.cdr.detectChanges();
    
    this.poNumberSubject$.next(this.poNumber);
  }

  savePONumber(poNumber?: string): void {
    const valueToSave = poNumber !== undefined ? poNumber : this.poNumber;
    const trimmedPONumber = valueToSave?.trim() || '';
    this.commonService.showSpinner$.next(true);
    this.knbrsalesCheckoutService.updatePONumber(trimmedPONumber).subscribe({
      next: () => {
        this.commonService.showSpinner$.next(false);
        this.poNumber = trimmedPONumber;
        this.poNumberError = '';
      },
      error: (error) => {
        this.commonService.showSpinner$.next(false);
        this.poNumberError = error?.error?.errors?.[0]?.message || 'Unable to save PO/Job Number.';
      }
    });
  }


  private updateCheckoutDisabled(): void {
    this.checkoutDisabled = false;
  }

  proceedToCheckout(): void {
    if (!this.cartId) {
      return;
    }

    this.routingService.goByUrl('/requestQuote');
  }

  isCheckoutDisabled(): boolean {
    return this.checkoutDisabled;
  }

  navigate(url: string): void {
    this.routingService.goByUrl(url);
  }

  goToDetail(item: any, event: Event): void {
    this.commonService.showSpinner$.next(true);
    let product = {
      code: item?.product?.baseProduct,
      defaultVariantProduct: item?.productCode,
      name: item?.name,
      salesOrg: item?.product?.salesOrg,
      slug: item?.slug,
      nameHtml: item?.nameHtml,
      prettyName: item?.prettyName,
    }
    const urlValue = this.routingService.getUrl({
      cxRoute: 'product',
      params: product
    });
    this.router.navigateByUrl(urlValue);
  }

  // ✅ Check if cart is an RFQ cart based on cartCategoryBrand
  private checkIfRfqCart(cart: any): boolean {
    const cartCategoryBrand = String((cart as any)?.cartCategoryBrand || '').toLowerCase();
    return cartCategoryBrand.includes('request a quote');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
